/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useRef, useState } from "react";

import { ChevronLeft, ChevronRight, Close, FilterList, FilterListOff } from "@mui/icons-material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import {
  Box,
  Button,
  Card,
  IconButton,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import useFetch from "src/Components/Common/useFetch";
import { userContext } from "src/Components/Contexts/userContext";
import { BusinessPartnersListForBcBetaRes } from "src/entity/recon-entity/ReconInterfaces";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { PartnerCommsContext } from "../../../Context/PartnerCommunicationContext";
import { NdAsyncButton, NdButton, NdButtonOutlined } from "../../../MsmePartnerPortal/CommonComponents";
import { StateDispatch } from "../../Common/Components";
import FilterAutocomplete from "../../Common/FilterAutocomplete";
import BcNewContext from "../BcNewContext";
import BalanceConfirmationNewFilter from "../BcNewFilter";
import CompanyBranchSelectModal from "../CompanyBranchSelectModal";
import ChooseCommsType from "./ChooseCommsType";
import SelectPartners from "./SelectPartners";
import SetupMail from "./SetupMail";

const Mailing = ({ setOpen }: { setOpen: StateDispatch<boolean> }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    activeStep,
    setActiveStep,
    mailingRows,
    setMailingRows,
    setRowsDataBcNewMailing,
    allRowsDataMailing,
    companyIdRef,
    branchCodeRef,
    setAppliedFiltersMailing,
  } = useContext(BcNewContext);
  const { communicationTypeId, setCommunicationTypeId, taskLabel, setTaskLabel, setEscalationStarted } =
    useContext(PartnerCommsContext);

  // const [openSchedulerDlg, setOpenSchedulerDlg] = useState<boolean>(false);

  const firstFoundCommType = mailingRows.find((row) => row.communicationTypeId);
  const allRowsMatched = mailingRows.every(
    (row) => row?.communicationTypeId !== firstFoundCommType?.communicationTypeId
  );

  const sendEmailRef = useRef<{ sendEmail: () => Promise<void> }>();

  const handlePageBack = () => {
    setOpen(false);
  };

  const handleBack = () => {
    if (firstFoundCommType && !allRowsMatched) {
      handlePageBack();
      return;
    }

    if (activeStep < 1) handlePageBack();
    else setActiveStep(activeStep - 1);
  };

  const handleNext = () => {
    if (activeStep >= 2) return;
    setActiveStep(activeStep + 1);
  };

  useEffect(() => {
    // Clean up Function: clear communication type id and task label and more
    return () => {
      setActiveStep(0);
      setCommunicationTypeId(null);
      setTaskLabel({ title: "" });
      setEscalationStarted(false);
      setMailingRows([]);
    };
  }, []);

  const ListAllBusinessPartners = async () => {
    setIsLoading(true);
    setAppliedFiltersMailing({});
    return useFetch<BusinessPartnersListForBcBetaRes>(
      API_ENDPOINTS.LIST_ALL_BUSINESS_PARTNERS_FOR_SENDING_MAIL.url,
      "GET",
      {
        failureMessage: API_ENDPOINTS.LIST_ALL_BUSINESS_PARTNERS_FOR_SENDING_MAIL.failureMessage,
        config: {
          params: {
            companyId: companyIdRef.current,
            branchCode: branchCodeRef.current,
          },
        },
        thenCallBack: (response) => {
          setRowsDataBcNewMailing(response.data?.businessPartnerList || []);
          allRowsDataMailing.current = response.data?.businessPartnerList || [];
          setIsLoading(false);
        },
        catchCallBack: () => {
          setIsLoading(false);
        },
      }
    );
  };

  const GetTaskLabelValidForPartnerComms = async (label: string) => {
    return useFetch<{ isValid: boolean }>(API_ENDPOINTS.GET_TASK_LABEL_VALIDATION.url, "POST", {
      failureMessage: API_ENDPOINTS.GET_TASK_LABEL_VALIDATION.failureMessage,
      showSuccessToast: true,
      data: {
        businessPartnerData: mailingRows.map((row) => {
          return { businessPartnerId: row.businessPartnerId, mappingId: row.mappingId };
        }),
        taskLabel: label,
      },
      thenCallBack: (res) => {
        if (res.data?.isValid) {
          setTaskLabel({ title: label });
          handleNext();
        } else {
          setTaskLabel({ title: "" });
        }
      },
      catchCallBack: (_err) => {
        setTaskLabel({ title: "" });
      },
    });
  };

  return (
    <>
      <Stack gap={1}>
        <HeaderSection setOpen={setOpen} ListAllBusinessPartners={ListAllBusinessPartners} />

        {/* Main Content */}
        <Card
          className="d_flex"
          sx={{ minHeight: 260, flexFlow: "column", borderRadius: "8px !important", mb: "90px", bgcolor: "#FAFAFA" }}
        >
          {/* Choose Comms Type */}
          {activeStep === 0 && <ChooseCommsType />}
          {/* Select Partners */}
          {activeStep === 1 && (
            <SelectPartners ListAllBusinessPartners={ListAllBusinessPartners} isLoading={isLoading} />
          )}
          {/* Setup Email */}
          {activeStep === 2 && <SetupMail ref={sendEmailRef} handleBack={handleBack} handlePageBack={handlePageBack} />}
        </Card>

        {/* Bottom Navigation Bar */}
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: -2,
            width: "100%",
            boxShadow: "0px -2px 4px 0px rgba(0, 0, 0, 0.08)",
            background: "#fff",
          }}
        >
          <Stack
            direction={"row"}
            sx={{
              justifyContent: "space-between",
              maxWidth: "900px",
              marginX: "auto",
              padding: "24px",
            }}
          >
            <Stack sx={{ display: "flex", flexDirection: "row", gap: "16px", ml: "auto" }}>
              <Button color="inherit" onClick={handleBack} startIcon={<ChevronLeft />}>
                Back
              </Button>
              <Box sx={{ flex: "1 1 auto" }} />
              {activeStep < 1 && (
                <NdButton
                  variant="contained"
                  disabled={activeStep === 0 ? !communicationTypeId : mailingRows.length === 0}
                  onClick={handleNext}
                  endIcon={<ChevronRight />}
                >
                  Next
                </NdButton>
              )}
              {activeStep === 1 && (
                <Tooltip
                  title={mailingRows.length > 600 ? "Total Selected Partners Should be Less than 600" : ""}
                  arrow
                >
                  <span className="NextButtonWrapper">
                    <NdAsyncButton
                      variant="contained"
                      disabled={mailingRows.length === 0 || mailingRows.length > 600}
                      onClick={async () => {
                        if (taskLabel?.title) await GetTaskLabelValidForPartnerComms(taskLabel?.title);
                        else handleNext();
                      }}
                      endIcon={<ChevronRight />}
                    >
                      Next
                    </NdAsyncButton>
                  </span>
                </Tooltip>
              )}
              {activeStep === 2 && (
                <NdAsyncButton
                  variant="contained"
                  onClick={async () => {
                    if (sendEmailRef?.current?.sendEmail) await sendEmailRef?.current.sendEmail();
                  }}
                >
                  Send Email
                </NdAsyncButton>
              )}
            </Stack>
          </Stack>
        </Box>
        {/* End of Bottom Navigation Bar */}
      </Stack>
    </>
  );
};

interface HeaderSectionProps {
  sx?: object;
  setOpen: (val: boolean) => void;
  ListAllBusinessPartners: any;
}

const HeaderSection: React.FC<HeaderSectionProps> = ({ sx, setOpen, ListAllBusinessPartners }) => {
  // Filter States
  const { actor } = useContext(userContext);
  const {
    storeAllCategories,
    activeStep,
    allRowsDataMailing,
    setRowsDataBcNewMailing,
    appliedFiltersMailing,
    setAppliedFiltersMailing,
    lastSelectedCompanyRef,
    lastSelectedBranchRef,
  } = useContext(BcNewContext);
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [openCompanyBranchFilter, setOpenCompanyBranchFilter] = useState<boolean>(false);

  return (
    <Paper
      elevation={1}
      sx={{
        width: "100%",
        minWidth: "600px",
        height: "auto",
        borderRadius: "8px",
        ...sx,
      }}
    >
      {/* Top Area */}

      <Box className="vertical_center_align" sx={{ padding: 3, borderBottom: "1px solid #0002" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mr: "auto" }}>
          <Typography variant="h5">New Mail</Typography>
          <Typography variant="body1">Send mail to single or bulk partner</Typography>
        </Box>

        {activeStep === 1 && (
          <>
            <NdButtonOutlined
              startIcon={Object.keys(appliedFiltersMailing).length > 0 ? <FilterList /> : <FilterListOff />}
              onClick={() => {
                setOpenFilter(true);
              }}
              sx={{ mr: 1 }}
            >
              Filter
            </NdButtonOutlined>
            {actor.integration && (
              <Tooltip title={actor.branchLevelReconcilation ? "Select Company and Branch" : "Select Company"}>
                <NdAsyncButton
                  sx={{ minWidth: "auto", ".MuiButton-startIcon": { mx: "auto" } }}
                  buttonComponent={NdButtonOutlined}
                  onClick={async () => {
                    setOpenCompanyBranchFilter(true);
                  }}
                  startIcon={<ApartmentIcon sx={{ mx: "auto", fontSize: "24px !important" }} />}
                />
              </Tooltip>
            )}
          </>
        )}

        <IconButton onClick={() => setOpen(false)}>
          <Close />
        </IconButton>
      </Box>

      {(Object.keys(appliedFiltersMailing).length > 0 || actor.integration) && (
        <Tabs
          value={false}
          onChange={() => null}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ pt: "12px", pb: "20px" }}
        >
          <Stack direction="row" spacing={2} className="space_between" sx={{ px: 3 }}>
            <Box className="d_flex vertical_center_align">
              {lastSelectedCompanyRef.current?.companyName && (
                <Box sx={{ mr: 2 }} key={"company"}>
                  <FilterAutocomplete
                    filterName={"company"}
                    filteredValues={[lastSelectedCompanyRef.current?.companyName]}
                    setAppliedFilters={() => {}}
                  />
                </Box>
              )}
              {lastSelectedBranchRef.current?.branchName && (
                <Box sx={{ mr: 2 }} key={"branch"}>
                  <FilterAutocomplete
                    filterName={"branch"}
                    filteredValues={[lastSelectedBranchRef.current?.branchName]}
                    setAppliedFilters={() => {}}
                  />
                </Box>
              )}
              {Object.keys(appliedFiltersMailing).map((key) =>
                appliedFiltersMailing[key].length ? (
                  <Box sx={{ mr: 2 }} key={key}>
                    <FilterAutocomplete
                      filterName={key}
                      filteredValues={appliedFiltersMailing[key]}
                      setAppliedFilters={setAppliedFiltersMailing}
                    />
                  </Box>
                ) : null
              )}
            </Box>
          </Stack>
        </Tabs>
      )}

      {/* Bottom Area */}
      <StepperSection />

      {/* Top Header Components */}
      {openFilter && (
        <BalanceConfirmationNewFilter
          allCategories={storeAllCategories.current}
          allRowsData={allRowsDataMailing.current}
          setRowsDataBalanceConfirmation={setRowsDataBcNewMailing}
          setAppliedFilters={setAppliedFiltersMailing}
          appliedFilters={appliedFiltersMailing}
          openFilter={openFilter}
          setOpenFilter={setOpenFilter}
        />
      )}
      {openCompanyBranchFilter && (
        <CompanyBranchSelectModal
          openFilter={openCompanyBranchFilter}
          setOpenFilter={setOpenCompanyBranchFilter}
          fetchData={ListAllBusinessPartners}
          setAppliedFilters={setAppliedFiltersMailing}
        />
      )}
    </Paper>
  );
};

HeaderSection.defaultProps = {
  sx: {},
};

const steps = ["Select Communication Type", "Select Business Partners", "Setup Email"];

export const StepperSection = () => {
  const { activeStep } = useContext(BcNewContext);

  return (
    <Box sx={{ width: "100%", borderRadius: "8px", background: "#FAFAFA" }}>
      <Stepper activeStep={activeStep} sx={{ px: 3, py: 1.5 }}>
        {steps.map((label, _index) => {
          const stepProps: { completed?: boolean } = {};

          return (
            <Step key={label} {...stepProps}>
              <StepLabel>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default Mailing;
